/**
 * useQuestionnaireConfig — fetches the server-driven questionnaire config.
 *
 *   GET /api/v1/customer/profile/questionnaire-config
 *
 * Per project decision (2026-05-09) the questionnaire is server-driven only —
 * no client-side fallback. If the fetch fails, the form surfaces a recoverable
 * error state with a retry. Cache strategy: 5-minute staleTime since the
 * config rarely changes; React Query retries twice with backoff before
 * surfacing the error.
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { ApiError, createKeyFactory } from '@finmin/api-client';
import { apiClient } from '@/lib/api';
import type { QuestionnaireConfig } from '../types';

export const questionnaireConfigKeys = createKeyFactory(
  'customer.questionnaire-config',
  {
    default: () => ['default'] as const,
  },
);

async function fetchQuestionnaireConfig(signal?: AbortSignal): Promise<QuestionnaireConfig> {
  const { data } = await apiClient.get<QuestionnaireConfig>(
    '/api/v1/customer/profile/questionnaire-config',
    { signal },
  );
  return data;
}

export function useQuestionnaireConfig(
  options?: Omit<UseQueryOptions<QuestionnaireConfig, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<QuestionnaireConfig, ApiError>({
    queryKey:  questionnaireConfigKeys.default(),
    queryFn:   ({ signal }) => fetchQuestionnaireConfig(signal),
    staleTime: 5 * 60 * 1000,
    retry:     2,
    ...options,
  });
}
